import React, {Component} from 'react';
import {Range} from 'rc-slider';
import './Slider.less';
import SliderHandle from '../SliderHandle/SliderHandle';
import debounce from 'lodash/debounce';

import type {Props as HandleProps} from '../SliderHandle/SliderHandle';
import classNames from 'classnames';

import {
    TrackingContext,
    TrackingContextValue,
} from '@homeaway/react-event-tracking';
import {GENERIC_EVENT} from '../../utils/edap';

type Props = {
    activeHandleIndex: number | null;
    className?: string;
    edapLabel?: string;
    max: number;
    min?: number;
    onChange: Function;
    onSetActiveHandle?: Function;
    start: number;
    step: number;
    stop: number;
};

type State = {
    start: number;
    stop: number;
};

const MIN_SPACING_INTERVAL = 20;

class Slider extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.triggerEdapEvent = debounce(this.triggerEdapEvent, 200, {
            leading: false,
            trailing: true,
        });

        this.state = {
            start: props.start,
            stop: props.stop,
        };
    }

    componentWillReceiveProps(nextProps: Props) {
        const {start, stop} = nextProps;
        this.setState({start, stop});
    }

    handleChange = (bounds: Array<number>) => {
        const {onSetActiveHandle, onChange, max, edapLabel} = this.props;
        const min = this.props.min || 0;
        const {start, stop} = this.state;
        const minSpacing = (max - min) / MIN_SPACING_INTERVAL;

        // if the bounds are too close, space them out to be as close as possible
        // without overlapping
        let newStart = bounds[0];
        let newStop = bounds[1];

        if (bounds[1] - bounds[0] < minSpacing) {
            // it's not possible to move both handles at the same time so at most only 1
            // bound will be changing
            if (bounds[0] !== start) {
                newStart = newStop - minSpacing;
            }
            if (bounds[1] !== stop) {
                newStop = newStart + minSpacing;
            }
        }

        const activeHandlerIndex: number = start !== newStart ? 0 : 1;
        if (edapLabel) {
            const eventlabel = `${edapLabel}${
                start !== newStart ? `_min_${newStart}` : `_max_${newStop}`
            }`;

            this.triggerEdapEvent(eventlabel);
        }

        this.setState({
            start: newStart,
            stop: newStop,
        });

        if (onSetActiveHandle) {
            onSetActiveHandle(activeHandlerIndex);
        }

        onChange([newStart, newStop]);
    };

    triggerEdapEvent = (eventlabel: string) => {
        const eventObj = {
            eventaction: 'filter selected',
            eventlabel,
        };
        const edapContext: TrackingContextValue = this.context;
        edapContext.trigger(GENERIC_EVENT, eventObj);
    };

    handleAfterChange = () => {
        const {onSetActiveHandle} = this.props;
        if (onSetActiveHandle) {
            onSetActiveHandle(null);
        }
    };

    connectedSliderHandle = (props: HandleProps) => {
        return (
            <SliderHandle
                {...props}
                key={props.index}
                onSetActiveHandle={this.props.onSetActiveHandle}
            />
        );
    };

    render() {
        const {min, max, step, className = '', activeHandleIndex} = this.props;
        const {start, stop} = this.state;

        const classes = classNames('Slider', {
            [className]: className,
            'active-handle-index-0': activeHandleIndex === 0,
            'active-handle-index-1': activeHandleIndex === 1,
        });

        return (
            <Range
                className={classes}
                min={min}
                max={max}
                step={step}
                value={[start, stop]}
                allowCross={false}
                onChange={this.handleChange}
                onAfterChange={this.handleAfterChange}
                handle={this.connectedSliderHandle}
            />
        );
    }
}

Slider.contextType = TrackingContext;

export default Slider;
